import React, { useLayoutEffect, useRef, useState } from "react";
import InvoicePreview from "./InvoicePreview";
import MealPlanPreview from "./MealPlanPreview";
import { LoadPhase, Skeleton, SkeletonCard } from "../../../components/ui";
import { BrandDetails, BrandPreviewId } from "../types/brand.types";

const DOCUMENTS: Array<{ id: BrandPreviewId; label: string }> = [
  { id: "invoice", label: "Invoice" },
  { id: "meal-plan", label: "Meal plan" },
];

/**
 * The documents are laid out at one canonical page size and scaled to
 * whatever width the column has. Type, rules and spacing all grow together,
 * so a wider panel is a bigger page rather than the same page with more
 * whitespace in it. 45:62 is the frame's aspect ratio.
 */
const PAGE_WIDTH = 450;
const PAGE_HEIGHT = (PAGE_WIDTH * 62) / 45;

/** Tailwind's `lg` — the width at which the panel becomes a fixed column. */
const LG_BREAKPOINT = 1024;

interface BrandPreviewPanelProps {
  details: BrandDetails;
  active: BrandPreviewId;
  onChange: (id: BrandPreviewId) => void;
  /**
   * Where the form's two records are. The document is drawn from specimen
   * values and could open the instant the panel mounts — but a page whose two
   * columns arrive one after the other reads as the screen arriving twice, so
   * it waits for the form and the pair land together.
   */
  phase?: LoadPhase;
}

/**
 * Live preview of what the form produces. The two documents share one frame
 * so switching between them never moves the panel.
 */
const BrandPreviewPanel: React.FC<BrandPreviewPanelProps> = ({
  details,
  active,
  onChange,
  phase = "ready",
}) => {
  const boxRef = useRef<HTMLDivElement>(null);
  const [scale, setScale] = useState(1);

  // The page is sized to whichever axis of the panel runs out first, so it is
  // always shown whole — never scrolled and never cropped.
  useLayoutEffect(() => {
    const box = boxRef.current;
    if (!box) return;

    const measure = () => {
      // Below `lg` the panel is not height-bound — it sits under the form and
      // takes whatever height the page needs — so only its width limits it.
      const heightBound = window.innerWidth >= LG_BREAKPOINT;

      // Fractional, unlike clientWidth/Height: rounding the box up by half a
      // pixel is enough to clip the page's bottom edge.
      const { width, height } = box.getBoundingClientRect();

      // From `lg` the column is sized by this box, so its width can't also be
      // what sizes the box. The ceiling comes from the `max-w-*` classes
      // below, which keeps the breakpoint sizes in the stylesheet.
      const ceiling = parseFloat(window.getComputedStyle(box).maxWidth);
      const byWidth = heightBound ? ceiling / PAGE_WIDTH : width / PAGE_WIDTH;
      const byHeight = heightBound ? height / PAGE_HEIGHT : Infinity;

      const next = Math.min(byWidth, byHeight);
      setScale(next > 0 && Number.isFinite(next) ? next : 1);
    };

    measure();
    window.addEventListener("resize", measure);

    // A window resize is not the only thing that changes the box: the web
    // font swapping in after first paint re-flows the header above it. A
    // measurement taken before that leaves the page taller than its box, and
    // the overflow is what crops the bottom of the document.
    const observer =
      typeof ResizeObserver === "undefined" ? null : new ResizeObserver(measure);
    observer?.observe(box);

    return () => {
      window.removeEventListener("resize", measure);
      observer?.disconnect();
    };
  }, []);

  const waiting = phase !== "ready";

  /** The page's footprint, which the block standing in for it takes too — so
   *  the document lands in the space its own placeholder held. */
  const page = { width: PAGE_WIDTH * scale, height: PAGE_HEIGHT * scale };

  return (
    <div className="flex flex-col lg:h-full lg:min-h-0">
      {/* The same pill group the Billings tab switches its two screens with:
          one rounded track holding both halves, and only the selected one
          tinted. `h-8` is shared with the form's section captions, which is
          what lines the two columns' cards up with each other — the pills
          stand a little taller than that, so they are centred on it rather
          than growing it and dropping this column below the other.

          It is a switch between two documents neither of which is on screen
          yet, so while the panel waits its own block stands here instead. */}
      <div
        role="group"
        aria-label="Preview document"
        className="mb-2 flex h-8 items-center justify-end"
      >
        {waiting ? (
          phase === "slow" && <Skeleton className="h-9 w-[180px] rounded-full" />
        ) : (
          <div className="inline-flex items-center gap-1 rounded-full border border-line bg-white p-1">
            {DOCUMENTS.map((doc) => {
              const selected = doc.id === active;
              return (
                <button
                  key={doc.id}
                  type="button"
                  aria-pressed={selected}
                  onClick={() => onChange(doc.id)}
                  className={`rounded-full px-3.5 py-1.5 text-[13px] 3xl:text-[14px] transition-colors ${
                    selected
                      ? "bg-[#BFF4FB] font-medium text-[#007E8C]"
                      : "text-ink-muted hover:text-ink"
                  }`}
                >
                  {doc.label}
                </button>
              );
            })}
          </div>
        )}
      </div>

      {/* The measured box is what the panel has to spend; the page below is
          sized to fit inside it, so nothing here ever scrolls. It is measured
          whether or not the document is in it yet, so the block below is
          already the size the page will be. */}
      <div
        ref={boxRef}
        className="lg:flex-1 lg:min-h-0 flex justify-end overflow-hidden lg:max-w-[400px] xl:max-w-[440px] 2xl:max-w-[480px] 3xl:max-w-[540px] 4xl:max-w-[620px]"
      >
        {waiting ? (
          // One block for the whole document, not for the rules and rows that
          // will be printed on it.
          <SkeletonCard phase={phase} style={page} />
        ) : (
          <div
            style={page}
            className="overflow-hidden rounded-md border border-line bg-white"
          >
            <div
              style={{
                width: PAGE_WIDTH,
                height: PAGE_HEIGHT,
                transform: `scale(${scale})`,
                transformOrigin: "top left",
              }}
            >
              {active === "invoice" ? (
                <InvoicePreview details={details} />
              ) : (
                <MealPlanPreview details={details} />
              )}
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default BrandPreviewPanel;
